import { Icon } from '@iconify/react'
import { formOptions, useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-form-adapter'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { addRole } from '~features/roles/api'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  CardFooter,
  IconButton,
  Input,
  Label,
  Textarea,
} from '~in-app/components'
import { auth$ } from '~in-app/legend-state'
import { rolesValidation } from '~in-app/validation/roles.validation'

export const Route = createFileRoute('/console/roles/add')({
  component: AddRolePage,
  staticData: {
    privileges: ['0.3.5.1'],
  },
})

function AddRolePage() {
  const router = useRouter()
  const addNewRoleFunc = () => {
    const mutation = useMutation({
      mutationFn: addRole,
      onSuccess: () => {
        router.navigate({ to: '/console/roles/list' })
        toast.success('New Role Added successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
        toast.error(`Error updating Roles status: ${error.message}`, { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
    })

    return mutation
  }
  const addNewRoleMutation = addNewRoleFunc()
  const submitRoles = async (value: any) => {
    try {
      const submitValue = JSON.parse(JSON.stringify(value))
      Object.entries(submitValue).forEach(([key, value]) => {
        if (value === '') {
          delete submitValue[key]
        }
      })
      addNewRoleMutation.mutate([submitValue])
    }
    catch (error) {
      console.error(error)
      toast.error(`Submit Error: ${error}`, {
        position: 'top-center',
        hideProgressBar: false,
        autoClose: 500,
      })
    }
  }

  const formOpts = formOptions({
    defaultValues: {
      name: '',
      desp: '',
      remarks: '',
      active: true,
    },
    validatorAdapter: zodValidator(),
    validators: {
      onChange: rolesValidation,
    },
    onSubmit: async ({ value }) => {
      // Handle empty values for 'address', 'desp', and 'remarks

      try {
        submitRoles(value)
      }
      catch (error) {
        console.error('Add Roles failed:', error)
        toast.error(`Error Submitting: ${error}`, {
          position: 'top-center',
          hideProgressBar: false,
          autoClose: 500,
        })
      }
    },
  })

  const formControl = useForm(formOpts)
  const [value, setValue] = useState(['item-1'])
  useEffect(() => {
    const validateFields = async () => {
      const fieldMeta = formControl.state.fieldMeta
      if (fieldMeta && formControl.state.errors.length > 0) {
        const fieldsToValidate = Object.keys(fieldMeta) as string[]
        const validationResults = await Promise.all(
          fieldsToValidate.map(async (field) => {
            const result = await formControl.validateField(field as any, 'change')
            return { field, result }
          }),
        )

        for (const { field, result: errors } of validationResults) {
          if (errors.length > 0) {
            const fieldElement = document.getElementById(field)
            fieldElement?.focus()
            // Scroll to the field element if it exists
            fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            break
          }
        }
      }
    }

    validateFields()
  }, [formControl.state.errors])

  return (
    <div>
      <div className="p-2">
        <IconButton
          color="muted"
          onClick={() => router.history.back()}
        >
          <Icon icon="lucide:arrow-left" />
        </IconButton>

        <span className="mb-4 ml-4 text-2xl font-bold">Add Roles Page</span>
      </div>

      <Card className="mt-4">
        <div className="p-2">
          <form
            noValidate
            onSubmit={
              (e) => {
                e.preventDefault()
                setValue(['item-1'])
                formControl.handleSubmit()
              }
            }
          >
            <CardContent className="mt-4 grid gap-4">
              {/* Role Information */}
              <Accordion
                type="multiple"
                value={value}
                onValueChange={setValue}
              >
                <AccordionItem value="item-1">
                  <AccordionTrigger>
                    <h4 className="text-lg font-semibold">Roles Information</h4>
                  </AccordionTrigger>

                  <AccordionContent className="w-full">
                    <div className="grid grid-cols-1 gap-1">
                      <div className="grid">
                        <formControl.Field
                          name="name"
                          validators={
                            {
                              onChange: rolesValidation.shape.name,
                            }
                          }
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    required
                                    className="text-xs"
                                    htmlFor="roleName"
                                  >
                                    Name
                                  </Label>

                                  <Input
                                    id="name"
                                    type="text"
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Description Field */}
                      <div className="grid gap-2">
                        <formControl.Field
                          name="desp"
                          validators={
                            {
                              onChange: rolesValidation.shape.desp,
                            }
                          }
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="roleDesp"
                                  >
                                    Description
                                  </Label>

                                  <Textarea
                                    id="desp"
                                    placeholder="Type your Description here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>

                      {/* Remarks Field */}
                      <div className="grid gap-2">
                        <formControl.Field
                          name="remarks"
                          validators={
                            {
                              onChange: rolesValidation.shape.remarks,
                            }
                          }
                        >
                          {
                            field => (
                              <>
                                <div className="relative">
                                  <Label
                                    className="text-xs"
                                    htmlFor="roleRemarks"
                                  >
                                    Remarks
                                  </Label>

                                  <Textarea
                                    id="remarks"
                                    placeholder="Type your Remarks here."
                                    rows={2}
                                    value={field.state.value}
                                    onBlur={field.handleBlur}
                                    onChange={
                                      e =>
                                        field.handleChange(e.target.value)
                                    }
                                  />
                                </div>

                                {
                                  field.state.meta.errors && (
                                    <em
                                      className="error-message"
                                      role="alert"
                                    >
                                      {field.state.meta.errors.join(', ')}
                                    </em>
                                  )
                                }
                              </>
                            )
                          }
                        </formControl.Field>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>

            <CardFooter>
              <formControl.Subscribe
                selector={state => [state.canSubmit, state.isSubmitting]}
              >
                {
                  ([canSubmit, isSubmitting]) => (
                    <Button
                      fullWidth
                      disabled={!canSubmit || !auth$.privileges.get()?.has('0.3.5.1')}
                    >
                      {isSubmitting ? '...' : 'Save'}
                    </Button>
                  )
                }
              </formControl.Subscribe>
            </CardFooter>
          </form>
        </div>
      </Card>
    </div>
  )
}
